import { FragmentOf, readFragment, ResultOf } from "gql.tada";
import { GetCaseStudyPageBySlug } from "@queries/GetCaseStudyPageBySlug";
import { SlideCarouselSectionFragment } from "@queries/fragments/SlideCarouselSectionFragment";
import { CaseStudyBodySectionFragment } from "@queries/fragments/CaseStudyBodySectionFragment";
import { TaggedEntryHero } from "@components/entries/TaggedEntryHero";
import { CaseStudyOverviewSection } from "@components/entries/CaseStudyOverviewSection";
import { CaseStudyBodySection } from "@components/entries/CaseStudyBodySection";
import { SlideCarouselSection } from "@components/entries/SlideCarouselSection";
import { FeaturedCard } from "@components/entries/FeaturedCard";
import { QuoteSlide } from "@components/entries/QuotationCarouselSection";
import { CaseStudyExploreMore } from "@components/entries/CaseStudyExploreMore";
import { BackgroundDeepContainer } from "./BackgroundDeepContainer";
import { RoutingSection } from "@components/entries/RoutingSection";
import { FootnoteProvider } from "@components/providers/FootnoteProvider";
import { FootnoteList } from "@components/shared/FootnoteList";
import { extractPageFootnotes } from "@utils/extractPageFootnotes";

type Page = NonNullable<
  NonNullable<ResultOf<typeof GetCaseStudyPageBySlug>["caseStudyPageCollection"]>["items"][number]
>;
type CarouselData = FragmentOf<typeof SlideCarouselSectionFragment>;

export const CaseStudyPageRenderer = ({
  data,
  carouselData = null,
}: {
  data: Page;
  carouselData?: CarouselData | null;
}) => {
  const carouselItems = carouselData
    ? readFragment(SlideCarouselSectionFragment, carouselData).itemsCollection?.items?.filter(
        (item): item is NonNullable<typeof item> => item !== null
      ) ?? []
    : [];

  const bodySectionBodies = (data.caseStudyBodyCollection?.items ?? [])
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .map((item) => ({ body: readFragment(CaseStudyBodySectionFragment, item)?.body }));

  const footnoteEntries = extractPageFootnotes([
    { body: data.overview?.descriptionRichText },
    ...(data.caseStudyInsights?.blocksCollection?.items ?? []),
    data.exploreMore,
    ...carouselItems.map((item) => ({ body: item.description })),
    ...bodySectionBodies,
  ]);

  return (
    <FootnoteProvider footnoteEntries={footnoteEntries}>
      {data.hero && <TaggedEntryHero entry={{ taggedEntryHero: data.hero }} />}
      <div className="px-6 dsk:px-[60px]">
        <div className="w-full max-w-[1260px] mx-auto">
          {(data.overview ||
            (data.caseStudyInsights?.blocksCollection?.items?.length ?? 0) > 0 ||
            data.adFormatsCarousel) && (
            <CaseStudyOverviewSection
              overview={data.overview}
              featuredProducts={carouselData}
              caseStudyInsights={data.caseStudyInsights}
            />
          )}
          {data.caseStudyBodyCollection?.items && data.caseStudyBodyCollection.items.length > 0 && (
            <div className="case-study-body flex flex-col gap-10 dsk:gap-[38px]">
              {data.caseStudyBodyCollection.items.map(
                (section, i) =>
                  section && (
                    <CaseStudyBodySection
                      key={section.sys.id}
                      entry={{ caseStudyBodySection: section }}
                      index={i}
                    />
                  )
              )}
            </div>
          )}
          {carouselData && (
            <SlideCarouselSection
              data={carouselData}
              renderCard={({ item, slideRef, onMouseEnter, onMouseLeave }) => (
                <FeaturedCard
                  item={item}
                  slideRef={slideRef}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                />
              )}
            />
          )}
          {data.highlightedQuotation && (
            <div className="py-20 dsk:py-[150px] flex items-center justify-center min-h-[600px]">
              <QuoteSlide
                quote={data.highlightedQuotation.quotation ?? ""}
                name={data.highlightedQuotation.attributionName}
                position={data.highlightedQuotation.attributionPosition}
                company={data.highlightedQuotation.attributionCompany}
              />
            </div>
          )}
          {data.exploreMore && <CaseStudyExploreMore data={data.exploreMore} />}
        </div>
      </div>
      {data.ctaFooter && (
        <BackgroundDeepContainer>
          <RoutingSection entry={{ routingSection: data.ctaFooter }} />
        </BackgroundDeepContainer>
      )}
      <FootnoteList />
    </FootnoteProvider>
  );
};
